<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { api } from '../api/client'
import { addDateDays, availabilityPeriodParams, calendarDateLabel, freeDateBooking, freeResourceBooking, monthLeadingDays, monthRange, objectAvailabilityParams, validateAvailabilityPeriod, validateObjectAvailability } from '../api/availability'
import { resourceTypeLabels } from '../api/mappers'
import { formatDateTime, toDateInput } from '../utils'

const props = defineProps({ resources: { type: Array, default: () => [] }, initialMonth: { type: String, default: '' } })
const emit = defineEmits(['book'])
const panel = ref(null)
const mode = ref('date')
const today = toDateInput()
const period = reactive({ startsAt: `${today}T14:00`, endsAt: `${addDateDays(today, 1)}T12:00`, resourceType: '', guests: '' })
const object = reactive({ resourceId: '', month: monthRange(props.initialMonth) ? props.initialMonth : today.slice(0, 7), startTime: '14:00', endTime: '12:00', endDayOffset: 1 })
const loading = ref(false)
const error = ref('')
const result = ref(null)
const timesEdited = ref(false)
let requestSequence = 0

const activeResources = computed(() => props.resources.filter((resource) => resource.isActive))
const freeResources = computed(() => (result.value?.resources || []).filter((resource) => resource.available === true))
const returnedDays = computed(() => result.value?.days || [])
const monthPadding = computed(() => monthLeadingDays(object.month))
const objectPeriodLabel = computed(() => `${object.startTime} → ${object.endTime}${Number(object.endDayOffset) ? ` через ${object.endDayOffset} дн` : ' в тот же день'}`)
const responsePeriodLabel = computed(() => result.value?.starts_at && result.value?.ends_at ? `${formatDateTime(result.value.starts_at)} — ${formatDateTime(result.value.ends_at)}` : '')

function invalidate() {
  ++requestSequence
  result.value = null
  error.value = ''
  loading.value = false
}

watch(() => [mode.value, period.startsAt, period.endsAt, period.resourceType, period.guests, object.resourceId, object.month, object.startTime, object.endTime, object.endDayOffset], invalidate, { flush: 'sync' })

function resourceDefaults() {
  if (timesEdited.value) return
  const selected = activeResources.value.find((resource) => String(resource.id) === String(object.resourceId))
  const hourly = selected?.priceUnit === 'per_hour'
  object.startTime = hourly ? '12:00' : '14:00'
  object.endTime = hourly ? '14:00' : '12:00'
  object.endDayOffset = hourly ? 0 : 1
}

watch(() => object.resourceId, resourceDefaults, { flush: 'sync' })
watch(() => activeResources.value.map((resource) => resource.id).join(','), () => {
  if (!activeResources.value.some((resource) => String(resource.id) === String(object.resourceId))) {
    object.resourceId = activeResources.value[0]?.id ?? ''
  }
  invalidate()
}, { immediate: true })

async function search() {
  invalidate()
  error.value = mode.value === 'date' ? validateAvailabilityPeriod(period) : validateObjectAvailability(object)
  if (error.value) return
  const sequence = ++requestSequence
  const requestedMode = mode.value
  const requestedId = object.resourceId
  const params = requestedMode === 'date' ? availabilityPeriodParams(period) : objectAvailabilityParams(object)
  loading.value = true
  try {
    const payload = requestedMode === 'date'
      ? await api.availability.search(params)
      : await api.availability.resource(requestedId, params)
    if (sequence === requestSequence) result.value = payload
  } catch (requestError) {
    if (sequence === requestSequence) error.value = requestError.message || 'Не удалось проверить доступность. Повторите поиск.'
  } finally {
    if (sequence === requestSequence) loading.value = false
  }
}

function bookResource(resource) {
  if (loading.value || resource.available !== true || !result.value) return
  const candidate = freeResourceBooking(result.value, resource.id)
  if (candidate) emit('book', candidate)
}

function bookDate(day) {
  if (loading.value || day.available !== true || !result.value) return
  const candidate = freeDateBooking(result.value, day.date)
  if (candidate) emit('book', candidate)
}

async function selectResource(id) {
  mode.value = 'object'
  object.resourceId = id
  if (monthRange(props.initialMonth)) object.month = props.initialMonth
  await nextTick()
  panel.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  await search()
}

async function refresh() {
  const shouldSearch = result.value != null || loading.value
  invalidate()
  if (shouldSearch) await search()
}

function dayStatus(day) {
  if (day.available === true) return 'Свободно'
  return day.busy_sources?.includes('avito') ? 'Занято · Авито' : 'Занято'
}

defineExpose({ selectResource, refresh })
</script>

<template>
  <section ref="panel" class="availability-panel" aria-labelledby="availability-heading">
    <header class="availability-header">
      <div><span class="eyebrow">Быстрый поиск</span><h2 id="availability-heading">Свободные объекты и даты</h2></div>
      <div class="availability-switch" role="group" aria-label="Режим поиска доступности">
        <button type="button" :aria-pressed="mode === 'date'" @click="mode = 'date'">Что свободно</button>
        <button type="button" :aria-pressed="mode === 'object'" @click="mode = 'object'">Даты объекта</button>
      </div>
    </header>

    <form v-if="mode === 'date'" class="availability-form" @submit.prevent="search">
      <label class="field"><span>Начало периода</span><input v-model="period.startsAt" type="datetime-local" required /></label>
      <label class="field"><span>Окончание периода</span><input v-model="period.endsAt" type="datetime-local" required /></label>
      <label class="field"><span>Тип объекта для поиска</span><select v-model="period.resourceType"><option value="">Все типы</option><option value="house">Дома</option><option value="bathhouse">Бани</option><option value="grill_house">Гриль-домики</option><option value="banquet_hall">Банкетные залы</option></select></label>
      <label class="field availability-guests"><span>Гостей, от</span><input v-model="period.guests" type="number" min="1" max="2147483647" step="1" placeholder="Любое число" /></label>
      <button type="submit" class="button button--primary" :disabled="loading || !activeResources.length">{{ loading ? 'Проверяем…' : 'Показать свободные' }}</button>
    </form>

    <form v-else class="availability-form availability-form--object" @submit.prevent="search">
      <label class="field availability-object"><span>Объект для поиска дат</span><select v-model="object.resourceId" required><option disabled value="">Выберите объект</option><option v-for="resource in activeResources" :key="resource.id" :value="resource.id">{{ resource.name }} · {{ resource.capacity }} чел.</option></select></label>
      <label class="field"><span>Месяц свободных дат</span><input v-model="object.month" type="month" required /></label>
      <label class="field"><span>Время начала</span><input v-model="object.startTime" type="time" required @input="timesEdited = true" /></label>
      <label class="field"><span>Время окончания</span><input v-model="object.endTime" type="time" required @input="timesEdited = true" /></label>
      <label class="field availability-offset"><span>Дней до окончания</span><input v-model="object.endDayOffset" type="number" min="0" max="30" step="1" required @input="timesEdited = true" /></label>
      <button type="submit" class="button button--primary" :disabled="loading || !activeResources.length">{{ loading ? 'Проверяем…' : 'Показать даты' }}</button>
    </form>

    <p class="availability-help">{{ mode === 'date' ? 'Свободно — объект доступен целиком на выбранный период, а не просто в течение части дня.' : `Даты начала брони: ${objectPeriodLabel}. 0 дней — окончание в тот же день, 1 — на следующий.` }} Время базы отдыха. Учитываются брони CRM и календарь Авито.</p>
    <p v-if="loading" class="availability-state" role="status">Проверяем актуальную занятость…</p>
    <p v-else-if="error" class="form-error" role="alert">{{ error }}</p>
    <template v-else-if="result">
      <div class="availability-summary" role="status">
        <strong v-if="mode === 'date'">Свободно {{ freeResources.length }} из {{ result.resources?.length || 0 }} подходящих объектов</strong>
        <strong v-else>{{ result.resource?.name }} · свободных дат начала: {{ result.available_count }} из {{ returnedDays.length }}</strong>
        <span v-if="responsePeriodLabel">{{ responsePeriodLabel }}</span>
      </div>
      <template v-if="mode === 'date'">
        <ul v-if="freeResources.length" class="availability-resources">
          <li v-for="resource in freeResources" :key="resource.id">
            <div><strong>{{ resource.name }}</strong><small>{{ resourceTypeLabels[resource.resource_type] }} · до {{ resource.capacity }} чел.</small></div>
            <button type="button" class="button button--secondary button--small" :aria-label="`Забронировать ${resource.name} на выбранный период`" @click="bookResource(resource)">Забронировать</button>
          </li>
        </ul>
        <p v-else class="availability-empty">На этот период свободных подходящих объектов нет. Измените даты, время, тип объекта или количество гостей.</p>
      </template>
      <div v-else class="availability-month" aria-label="Свободные даты выбранного объекта">
        <div class="availability-weekdays" aria-hidden="true"><span v-for="weekday in ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']" :key="weekday">{{ weekday }}</span></div>
        <div class="availability-days">
          <span v-for="index in monthPadding" :key="`blank-${index}`" aria-hidden="true" />
          <button v-for="day in returnedDays" :key="day.date" type="button" :disabled="day.available !== true" :class="day.available === true ? 'availability-day--free' : 'availability-day--busy'" :aria-label="`${calendarDateLabel(day.date)}: ${dayStatus(day)}${day.available === true ? ', создать бронь' : ''}`" :title="`${calendarDateLabel(day.date)} · ${formatDateTime(day.starts_at)} — ${formatDateTime(day.ends_at)} · ${dayStatus(day)}`" @click="bookDate(day)"><strong>{{ Number(day.date.slice(-2)) }}</strong><span>{{ day.available === true ? 'Свободно' : day.busy_sources?.includes('avito') ? 'Авито' : 'Занято' }}</span></button>
        </div>
      </div>
      <p class="availability-help availability-snapshot">Проверено {{ formatDateTime(result.checked_at) }}. Занятость может измениться: при сохранении брони сервер проверит её ещё раз. Нажмите на свободный объект или дату, чтобы открыть бронь с этим периодом.</p>
    </template>
    <p v-else class="availability-empty">{{ activeResources.length ? 'Укажите период или выберите объект и нажмите поиск.' : 'Для поиска нужны активные объекты. Их добавляет администратор.' }}</p>
  </section>
</template>

<style scoped>
.availability-panel { background: var(--paper-strong); border: 1px solid var(--line); border-radius: var(--radius); padding: 22px; margin-bottom: 24px; scroll-margin-top: 20px; }
.availability-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.availability-header h2 { font-size: 1.08rem; margin: 5px 0 0; }
.availability-switch { display: flex; flex-shrink: 0; padding: 4px; border: 1px solid var(--line); border-radius: 9px; background: var(--milk); }
.availability-switch button { border: 0; background: transparent; padding: 9px 13px; border-radius: 6px; font-size: .78rem; font-weight: 650; cursor: pointer; }
.availability-switch [aria-pressed="true"] { background: var(--forest); color: white; }
.availability-form { display: grid; grid-template-columns: 1.35fr 1.35fr 1fr .7fr auto; gap: 12px; align-items: end; }
.availability-form .field { margin: 0; min-width: 0; }
.availability-form .field > span { font-size: .7rem; }
.availability-form input, .availability-form select { min-width: 0; width: 100%; }
.availability-form > .button { white-space: nowrap; }
.availability-form--object { grid-template-columns: 1.6fr 1.1fr .7fr .7fr .7fr auto; }
.availability-help, .availability-empty, .availability-state { color: var(--muted); font-size: .75rem; line-height: 1.5; margin: 13px 0 0; }
.availability-summary { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; border-top: 1px solid var(--line); margin-top: 17px; padding-top: 16px; font-size: .8rem; }
.availability-summary > span { color: var(--muted); font-size: .75rem; }
.availability-resources { list-style: none; padding: 0 4px 0 0; margin: 12px 0 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px 18px; max-height: 300px; overflow-y: auto; }
.availability-resources li { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--line); }
.availability-resources strong { font-size: .82rem; overflow-wrap: anywhere; }
.availability-resources small { display: block; margin-top: 4px; color: var(--muted); font-size: .72rem; }
.availability-month { margin-top: 18px; max-width: 850px; }
.availability-weekdays, .availability-days { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 7px; }
.availability-weekdays { color: var(--muted); font-size: .72rem; text-align: center; margin-bottom: 7px; }
.availability-days button { min-width: 0; border: 1px solid var(--line); border-radius: 7px; min-height: 60px; padding: 9px 5px; display: grid; place-content: center; gap: 4px; text-align: center; }
.availability-days button strong { font-size: .9rem; }
.availability-days button span { font-size: .67rem; }
.availability-day--free { color: var(--forest); background: var(--forest-pale); cursor: pointer; }
.availability-day--free:hover { border-color: var(--forest); }
.availability-days .availability-day--busy { color: var(--muted); background: var(--milk); opacity: 1; cursor: default; }
.availability-snapshot { margin-top: 14px; }
@media (max-width: 1150px) { .availability-form { grid-template-columns: 1fr 1fr 1fr; } .availability-form--object { grid-template-columns: 1.4fr 1fr 1fr; } }
@media (max-width: 700px) { .availability-panel { padding: 16px; } .availability-header { align-items: stretch; flex-direction: column; } .availability-switch { align-self: flex-start; } .availability-form, .availability-form--object { grid-template-columns: 1fr 1fr; } .availability-object { grid-column: 1 / -1; } .availability-form > .button { grid-column: 1 / -1; } .availability-resources { grid-template-columns: 1fr; } }
@media (max-width: 420px) { .availability-form { grid-template-columns: 1fr; } .availability-days, .availability-weekdays { gap: 4px; } .availability-days button span { font-size: .57rem; } .availability-days button { min-height: 52px; padding-inline: 1px; } }
</style>
